import {
  IUserRepository,
  CreateUserCommand,
  UpdateUserCommand,
  AssignRoleCommand,
  LinkBusinessCommand
} from '../../domain/interfaces/IUserRepository';
import { User } from '../../domain/entities/User';
import { PaginatedResult, PaginationParams } from '../../domain/common/PaginatedResult';
import { ApiClient } from '../api/ApiClient';
import { API_ENDPOINTS } from '../api/config';
import {
  UserListItemDto,
  UserDetailDto,
  AssignBusinessResponse
} from '../../application/dto/UserDtos';

/**
 * UserRepository - Implementation of user repository
 *
 * Handles all API calls related to users
 * Updated with Sprint 010 backend enhancements
 */
export class UserRepository implements IUserRepository {
  constructor(private readonly apiClient: ApiClient) {}

  /**
   * Get all users with pagination (admin only)
   * Returns UserListItemDto with business information
   */
  async getAll(params?: PaginationParams): Promise<PaginatedResult<UserListItemDto>> {
    return this.apiClient.get<PaginatedResult<UserListItemDto>>(API_ENDPOINTS.USERS.BASE, {
      params
    });
  }

  /**
   * Get a single user by ID
   * Returns UserDetailDto with complete information
   */
  async getById(id: string): Promise<UserDetailDto> {
    return this.apiClient.get<UserDetailDto>(API_ENDPOINTS.USERS.BY_ID(id));
  }

  /**
   * Create a new user (admin only)
   */
  async create(command: CreateUserCommand): Promise<User> {
    return this.apiClient.post<User>(API_ENDPOINTS.USERS.BASE, command);
  }

  /**
   * Update an existing user
   */
  async update(id: string, command: UpdateUserCommand): Promise<User> {
    return this.apiClient.put<User>(API_ENDPOINTS.USERS.BY_ID(id), command);
  }

  /**
   * Delete a user (admin only)
   */
  async delete(id: string): Promise<void> {
    await this.apiClient.delete<void>(API_ENDPOINTS.USERS.BY_ID(id));
  }

  /**
   * Assign a role to a user (admin only)
   */
  async assignRole(command: AssignRoleCommand): Promise<void> {
    await this.apiClient.post<void>(
      `${API_ENDPOINTS.USERS.BY_ID(command.userId)}/assign-role`,
      { role: command.role }
    );
  }

  /**
   * Link a customer user to a business (admin only)
   * @deprecated Use assignBusiness instead (Sprint 010)
   */
  async linkBusinessToCustomer(command: LinkBusinessCommand): Promise<void> {
    await this.apiClient.post<void>(
      `${API_ENDPOINTS.USERS.BY_ID(command.userId)}/link-business`,
      { businessId: command.businessId }
    );
  }

  /**
   * Assign a business to a user (admin only)
   * Sprint 010 - New endpoint
   */
  async assignBusiness(userId: string, businessId: string): Promise<AssignBusinessResponse> {
    return this.apiClient.put<AssignBusinessResponse>(
      API_ENDPOINTS.USERS.ASSIGN_BUSINESS(userId),
      { businessId }
    );
  }

  /**
   * Remove a business from a user (admin only)
   * Sprint 010 - New endpoint
   */
  async removeBusiness(userId: string): Promise<void> {
    await this.apiClient.delete<void>(API_ENDPOINTS.USERS.REMOVE_BUSINESS(userId));
  }
}
